import moment from 'moment-timezone';
import { useEffect, useId, useState } from 'react';
import { useIntl } from 'react-intl';
import { Range, useQueryFetch } from 'sonata-react-components';

import { getExtendedCampaignEnd } from '@/components/driveToStore/common/helpers/campaignDates.helper';
import { getDriveToStoreReportUrl } from '@/components/driveToStore/common/hooks/useDriveToStoreNavigation';
import PeriodNavigator from '@/components/driveToStore/common/PeriodNavigator/PeriodNavigator';
import { isSharedReportSession } from '@/helpers/shareReport/shareReport.helper';
import { getHeatmapData } from '@/services/footfall.service';
import { useGlobalStore } from '@/store/global';
import { getSession } from '@/store/global/session/selectors';
import SelectLineItemPrompt from './StoreHeatmap/SelectLineItemPrompt/SelectLineItemPrompt';
import StoreHeatmap from './StoreHeatmap/StoreHeatmap';
import VisitationMetricTrendsHeader from './VisitationMetricTrendsHeader/VisitationMetricTrendsHeader';
import VisitationMetricTrendsSkeleton from './VisitationMetricTrendsSkeleton';

import type { DateInterval, HeatmapPoiData } from '@/services/footfall.service';
import type { MetricFilter, Poi, StoreRow, TagItem } from '@/types/footfall';

import './visitationMetricTrends.scss';

const RANGE_MIN = 0;

function buildRangeMarks(max: number): Record<number, string> {
  const marks: Record<number, string> = {};
  const STEP_COUNT = 5;
  const step = Math.max(1, Math.ceil(max / STEP_COUNT));
  for (let val = 0; val <= max; val += step) {
    marks[val] = String(val);
  }
  marks[max] = String(max);
  return marks;
}

const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;

const EMPTY_METRIC_BY_DAY = {
  average: null,
  friday: null,
  monday: null,
  saturday: null,
  sunday: null,
  thursday: null,
  tuesday: null,
  wednesday: null
};

type LinkedLineItemPrompt = {
  poiId: string;
  options: { id: string; name: string }[];
};

type State = {
  weekOffset: number;
  metricFilter: MetricFilter;
  activeTagIds: string[];
  emptyTrailingWeeks: number;
  rangeState: {
    lastRangeMax: number;
    rangeValue: [number, number];
  };
  linkedLineItemPrompt: LinkedLineItemPrompt | null;
};

type Props = {
  campaignEnd: string;
  campaignStart: string;
  entityId: string;
  entityType: string;
  lineItems: { id: string; name: string }[];
  pois: Poi[];
  sharedReportFullAccess?: boolean;
  timezone: string;
  venueIds?: string[];
  userId: string;
};

function computeMetricValue(point: HeatmapPoiData['dailyData'][number], metricFilter: MetricFilter): number | null {
  switch (metricFilter) {
    case 'uplift':
    case 'percentage':
      return point.uplift;
    case 'baseline':
      return point.baseline;
    case 'cpsv':
      return point.cpsv;
    default:
      return null;
  }
}

function buildStores(
  heatmapData: HeatmapPoiData[],
  pois: Poi[],
  metricFilter: MetricFilter,
  weekStart: string,
  campaignStart: string,
  campaignEnd: string,
  timezone: string
): StoreRow[] {
  const weekStartMoment = moment.tz(weekStart, timezone).startOf('day');
  const campaignStartMoment = moment.tz(campaignStart, timezone).startOf('day');
  const campaignEndMoment = moment.tz(campaignEnd, timezone).endOf('day');
  const todayMoment = moment.tz(timezone).startOf('day');

  return heatmapData.map((poi, index) => {
    const matchingPoi = pois.find(p => p.venueId === poi.venueId);
    const metricByDay: Record<string, number | null> = { ...EMPTY_METRIC_BY_DAY };
    const isOutsidePeriod: Record<string, boolean> = {};
    const isNoData: Record<string, boolean> = {};
    const daysWithData = new Set<string>();
    const dayValues: number[] = [];

    poi.dailyData.forEach(point => {
      const pointMoment = moment.tz(point.date, timezone).startOf('day');
      // Backend returns 0 for future dates — ignore them so they render as noData
      if (!pointMoment.isBefore(todayMoment)) {
        return;
      }
      const dayName = DAY_NAMES[pointMoment.day()];
      if (dayName === undefined) {
        return;
      }
      const value = computeMetricValue(point, metricFilter);
      if (value !== null) {
        metricByDay[dayName] = value;
        dayValues.push(value);
        daysWithData.add(dayName);
      }
    });

    Array.from({ length: 7 }, (_, i) => i).forEach(i => {
      const dayName = DAY_NAMES[(i + 1) % 7];
      if (dayName === undefined) {
        return;
      }
      const dayMoment = weekStartMoment.clone().add(i, 'days');
      if (dayMoment.isBefore(campaignStartMoment) || dayMoment.isAfter(campaignEndMoment)) {
        isOutsidePeriod[dayName] = true;
      } else if (!daysWithData.has(dayName)) {
        isNoData[dayName] = true;
      }
    });

    if (metricFilter === 'percentage') {
      metricByDay.average = poi.upliftAvg;
    } else if (dayValues.length > 0) {
      metricByDay.average = dayValues.reduce((a, b) => a + b, 0) / dayValues.length;
    }

    return {
      address: poi.address,
      bestTimeRange: poi.peakTime ?? undefined,
      isNoData,
      isOutsidePeriod,
      lineItemIds: poi.lineItemIds,
      metricByDay,
      name: poi.name,
      poiId: matchingPoi?.id,
      rank: index + 1
    };
  });
}

export default function VisitationMetricTrends({
  campaignEnd,
  campaignStart,
  entityId,
  entityType,
  lineItems,
  pois,
  sharedReportFullAccess,
  timezone,
  venueIds,
  userId
}: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const rangeId = useId();
  const session = useGlobalStore(getSession);
  const isShared = isSharedReportSession(session);
  const canOpenSingleReport = !isShared || sharedReportFullAccess;

  const [{ weekOffset, metricFilter, activeTagIds, emptyTrailingWeeks, rangeState, linkedLineItemPrompt }, setState] =
    useState<State>({
      activeTagIds: [],
      emptyTrailingWeeks: 0,
      linkedLineItemPrompt: null,
      metricFilter: 'percentage',
      rangeState: { lastRangeMax: 0, rangeValue: [RANGE_MIN, 0] },
      weekOffset: 0
    });

  const extendedCampaignEnd = getExtendedCampaignEnd(campaignEnd, timezone);

  const firstWeekStart = moment.tz(campaignStart, timezone).startOf('isoWeek');
  const totalWeeks = moment.tz(extendedCampaignEnd, timezone).startOf('isoWeek').diff(firstWeekStart, 'weeks') + 1;
  const effectiveTotalWeeks = Math.max(1, totalWeeks - emptyTrailingWeeks);
  const nowTz = moment.tz(timezone);
  const mondayOffset = nowTz.isoWeekday() === 1 ? 1 : 0;
  const currentWeekIndex = campaignStart
    ? Math.max(
        0,
        Math.min(nowTz.clone().startOf('isoWeek').diff(firstWeekStart, 'weeks') - mondayOffset, effectiveTotalWeeks - 1)
      )
    : 0;
  const weekIndex = Math.max(0, Math.min(currentWeekIndex + weekOffset, effectiveTotalWeeks - 1));
  const weekStart = firstWeekStart.clone().add(weekIndex, 'weeks');
  const weekEnd = weekStart.clone().endOf('isoWeek');
  const weekLabels = Array.from({ length: effectiveTotalWeeks }, (_, i) => {
    const ws = firstWeekStart.clone().add(i, 'weeks');
    const we = ws.clone().endOf('isoWeek');
    return `${ws.format('MMMM D')} – ${we.format('MMMM D')}`;
  });
  const dateInterval: DateInterval = {
    end: weekEnd.toISOString(),
    start: weekStart.toISOString(),
    timezone: timezone
  };

  const brandFilteredIds =
    activeTagIds.length > 0
      ? pois
          .filter(p => p.brand !== undefined && activeTagIds.includes(p.brand) && p.venueId !== undefined)
          .map(p => p.venueId!)
      : undefined;

  const filteredVenueIds = (() => {
    if (venueIds?.length && brandFilteredIds?.length) {
      return brandFilteredIds.filter(id => venueIds.includes(id));
    }
    if (venueIds?.length) {
      return venueIds;
    }
    return brandFilteredIds;
  })();

  const trendsRequestToken: Record<string, any> = {};
  useEffect(() => {
    return () => {
      trendsRequestToken.cancel?.();
    };
  }, []);

  const { data: heatmapData = [], isLoading: isLoadingHeatmap } = useQueryFetch<HeatmapPoiData[]>(
    [
      'footfall',
      'heatmap',
      entityType,
      entityId,
      weekIndex,
      metricFilter,
      venueIds?.join(',') ?? '',
      filteredVenueIds?.join(',') ?? ''
    ],
    async () =>
      await getHeatmapData(
        entityType,
        entityId,
        userId,
        dateInterval,
        metricFilter,
        filteredVenueIds,
        trendsRequestToken
      ),
    { enabled: !!campaignStart }
  );

  const isLastNavigableWeek = weekIndex === effectiveTotalWeeks - 1;
  if (!isLoadingHeatmap && heatmapData.length === 0 && isLastNavigableWeek && effectiveTotalWeeks > 1) {
    setState(prev => ({ ...prev, emptyTrailingWeeks: prev.emptyTrailingWeeks + 1 }));
  }

  const stores = buildStores(
    heatmapData,
    pois,
    metricFilter,
    weekStart.format('YYYY-MM-DD'),
    campaignStart,
    extendedCampaignEnd,
    timezone
  );

  const heatmapMax = stores.reduce((acc, store) => {
    return Object.values(store.metricByDay).reduce<number>((m, val) => (val !== null && val > m ? val : m), acc);
  }, 0);
  const rangeMax = Math.ceil(heatmapMax > 0 ? heatmapMax : 4);

  const rangeValue: [number, number] =
    rangeState.lastRangeMax === rangeMax ? rangeState.rangeValue : [RANGE_MIN, rangeMax];
  if (rangeState.lastRangeMax !== rangeMax) {
    setState(prev => ({
      ...prev,
      rangeState: {
        lastRangeMax: rangeMax,
        rangeValue: [RANGE_MIN, rangeMax]
      }
    }));
  }

  const tags: TagItem[] = [...new Set(pois.filter(p => p.brand !== undefined).map(p => p.brand!))].map(brand => ({
    id: brand,
    label: brand
  }));

  if (isLoadingHeatmap) {
    return <VisitationMetricTrendsSkeleton />;
  }

  return (
    <div className="visitationMetricTrends">
      <VisitationMetricTrendsHeader
        activeTagIds={activeTagIds}
        metricFilter={metricFilter}
        onAllTagsClick={() => setState(prev => ({ ...prev, activeTagIds: [] }))}
        onMetricFilterChange={metric => setState(prev => ({ ...prev, metricFilter: metric }))}
        onTagClick={tagId =>
          setState(prev => ({
            ...prev,
            activeTagIds: prev.activeTagIds.includes(tagId)
              ? prev.activeTagIds.filter(id => id !== tagId)
              : [...prev.activeTagIds, tagId]
          }))
        }
        tags={tags}
      />
      <div className="visitationMetricTrends__body">
        <div className="visitationMetricTrends__heatmap">
          <StoreHeatmap
            metricFilter={metricFilter}
            onOpenReport={
              canOpenSingleReport
                ? poiId => {
                    const store = stores.find(s => s.poiId === poiId);
                    const linkedIds = store?.lineItemIds ?? [];
                    if (linkedIds.length > 1) {
                      setState(prev => ({
                        ...prev,
                        linkedLineItemPrompt: { options: lineItems.filter(li => linkedIds.includes(li.id)), poiId }
                      }));
                      return;
                    }
                    window.open(
                      getDriveToStoreReportUrl(entityType, entityId, { poiId }),
                      '_blank',
                      'noopener,noreferrer'
                    );
                  }
                : undefined
            }
            rangeValue={rangeValue}
            stores={stores}
            timezone={timezone}
            weekStart={weekStart.format('YYYY-MM-DD')}
          />
          {linkedLineItemPrompt && (
            <SelectLineItemPrompt
              entityType={entityType}
              lineItems={linkedLineItemPrompt.options}
              onClose={() => setState(prev => ({ ...prev, linkedLineItemPrompt: null }))}
              poiId={linkedLineItemPrompt.poiId}
            />
          )}
        </div>
        <footer className="visitationMetricTrends__controls">
          <div className="visitationMetricTrends__controlsLeft">
            <p className="visitationMetricTrends__controlsNote">
              {formatMessage({
                defaultMessage: 'Color legend',
                id: 'driveToStore.aggregatedReport.visitationMetricTrends.dataNote'
              })}
            </p>
            <p className="visitationMetricTrends__controlsLegend">
              {formatMessage({
                defaultMessage:
                  'Color intensity represents the value per day, where darker tones indicate higher visitor growth, organic traffic (baseline) or CPSV (Cost Per Store Visit) depending on your filters.',
                id: 'driveToStore.aggregatedReport.visitationMetricTrends.rangeLabel'
              })}
            </p>
          </div>
          <div className="visitationMetricTrends__controlsRight">
            <div className="visitationMetricTrends__weekNavContainer">
              <span className="visitationMetricTrends__weekNavTitle">
                {formatMessage({
                  defaultMessage: 'Selected week',
                  id: 'aggregatedReport.visitationMetricTrends.rangeFilter'
                })}
              </span>
              <PeriodNavigator
                ariaLabels={{ first: 'First week', last: 'Last week', next: 'Next week', previous: 'Previous week' }}
                onChange={newIndex => setState(prev => ({ ...prev, weekOffset: newIndex - currentWeekIndex }))}
                periodIndex={weekIndex}
                periodLabels={weekLabels}
                totalPeriods={effectiveTotalWeeks}
              />
            </div>
            <Range
              className="visitationMetricTrends__rangeSlider"
              id={rangeId}
              max={rangeMax}
              min={RANGE_MIN}
              onChange={value => {
                const v = value as number[];
                setState(prev => ({
                  ...prev,
                  rangeState: {
                    lastRangeMax: rangeMax,
                    rangeValue: [v[0] ?? RANGE_MIN, v[1] ?? rangeMax]
                  }
                }));
              }}
              type="range"
              value={rangeValue}
            >
              <Range.Box
                title={formatMessage(
                  metricFilter === 'baseline'
                    ? {
                        defaultMessage: 'Baseline filter',
                        id: 'aggregatedReport.visitationMetricTrends.baselineFilter'
                      }
                    : metricFilter === 'cpsv'
                      ? {
                          defaultMessage: 'CPSV filter',
                          id: 'aggregatedReport.visitationMetricTrends.cpsvFilter'
                        }
                      : {
                          defaultMessage: 'Uplift filter',
                          id: 'aggregatedReport.visitationMetricTrends.upliftFilter'
                        }
                )}
              >
                <Range.Bar marks={buildRangeMarks(rangeMax)} />
              </Range.Box>
            </Range>
          </div>
        </footer>
      </div>
    </div>
  );
}
